import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BaseInputEditor,
  type BaseInputEditorVisibilityProps,
} from "@/components/common/BaseInputEditor";
import type { DraftStorageError } from "@/features/composer-drafts/draftRepository";
import type { ComposerDraftScope } from "@/features/composer-drafts/types";
import type { User } from "@/lib/humhub-api/types";
import { cn } from "@/lib/utils";

interface BaseComposeInputProps extends BaseInputEditorVisibilityProps {
  placeholder?: string;
  submitButtonLabel?: string;
  cancelButtonLabel?: string;
  onCancel?: () => void;
  showFormatting?: boolean;
  showAttachButton?: boolean;
  onContentChange?: (content: string) => void;
  submitDisabled?: boolean;
  hasDiscardableChanges?: boolean;
  externalOnAttachClick?: () => void;
  externalFileInputRef?: React.RefObject<HTMLInputElement | null>;
  externalOnFileChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit?: (content: string, files?: File[]) => Promise<void>;
  className?: string;
  containerClassNameOverride?: string;
  submitIconOnly?: boolean;
  disabled?: boolean;
  maxFileSize: number;
  maxFileCount: number;
  maxLength: number;
  allowedFileTypes?: string[];
  mentionUsers?: User[];
  isLoadingMentionUsers?: boolean;
  hasMoreMentionUsers?: boolean;
  isFetchingMoreMentionUsers?: boolean;
  mentionLoadMoreError?: string | null;
  onLoadMoreMentionUsers?: () => Promise<User[]>;
  onMentionTrigger?: (query: string) => Promise<User[]>;
  mentionScopeKey?: string;
  autoFocus?: boolean;
  compact?: boolean;
  searchLikeWhenBlurred?: boolean;
  context?: "post" | "chat";
  initialContent?: string;
  initialFiles?: File[];
  initialDraftKey?: string;
  draftScope?: ComposerDraftScope;
  onDraftRestored?: () => void;
  onDraftRestorationSettled?: () => void;
  onDraftStorageError?: (error: DraftStorageError) => void;
  draftTargetUnavailable?: boolean;
}

export const BaseComposeInput: React.FC<BaseComposeInputProps> = ({
  placeholder,
  submitButtonLabel,
  cancelButtonLabel,
  onCancel,
  showFormatting = true,
  showAttachButton = true,
  onContentChange,
  submitDisabled = false,
  hasDiscardableChanges,
  externalOnAttachClick,
  externalFileInputRef,
  externalOnFileChange,
  onSubmit,
  className = "",
  containerClassNameOverride,
  submitIconOnly = false,
  disabled,
  maxFileSize,
  maxFileCount,
  maxLength,
  allowedFileTypes,
  mentionUsers = [],
  isLoadingMentionUsers = false,
  hasMoreMentionUsers = false,
  isFetchingMoreMentionUsers = false,
  mentionLoadMoreError,
  onLoadMoreMentionUsers,
  onMentionTrigger,
  mentionScopeKey,
  autoFocus = false,
  compact = false,
  searchLikeWhenBlurred = false,
  context = "post",
  alwaysVisible,
  initialContent,
  initialFiles,
  initialDraftKey,
  draftScope,
  onDraftRestored,
  onDraftRestorationSettled,
  onDraftStorageError,
  draftTargetUnavailable,
}) => {
  const { t } = useTranslation();
  const [isEditorFocused, setIsEditorFocused] = useState(false);
  const isSearchLikeBlurred = searchLikeWhenBlurred && !isEditorFocused;

  useEffect(() => {
    const collapseForPostEdit = () => setIsEditorFocused(false);
    window.addEventListener("stream-post-edit-start", collapseForPostEdit);
    return () =>
      window.removeEventListener("stream-post-edit-start", collapseForPostEdit);
  }, []);

  const containerClassName = cn(
    "w-full rounded-tr-lg border bg-gray-50 shadow-sm transition-all duration-200 [&_.base-input-editor]:leading-6",
    isSearchLikeBlurred
      ? "h-auto !border-0 !bg-transparent px-0 py-0 shadow-none"
      : compact
        ? "border-gray-100 p-2"
        : "border-gray-100 p-3 [@media(max-height:700px)]:p-1.5",
    containerClassNameOverride,
  );

  const editorClassName = cn(
    "base-input-editor",
    isSearchLikeBlurred && "min-h-0 leading-5",
    className,
  );

  return (
    <div className={containerClassName}>
      <BaseInputEditor
        placeholder={placeholder || t("common.inputPlaceholder")}
        submitButtonLabel={submitButtonLabel || t("common.create")}
        cancelButtonLabel={cancelButtonLabel}
        onCancel={onCancel}
        showFormatting={showFormatting}
        showAttachButton={showAttachButton}
        onContentChange={onContentChange}
        submitDisabled={submitDisabled}
        hasDiscardableChanges={hasDiscardableChanges}
        externalOnAttachClick={externalOnAttachClick}
        externalFileInputRef={externalFileInputRef}
        externalOnFileChange={externalOnFileChange}
        onSubmit={onSubmit}
        className={editorClassName}
        disabled={disabled}
        maxFileSize={maxFileSize}
        maxFileCount={maxFileCount}
        allowedFileTypes={allowedFileTypes}
        onMentionTrigger={onMentionTrigger}
        mentionScopeKey={mentionScopeKey}
        mentionUsers={mentionUsers}
        isLoadingMentionUsers={isLoadingMentionUsers}
        hasMoreMentionUsers={hasMoreMentionUsers}
        isFetchingMoreMentionUsers={isFetchingMoreMentionUsers}
        mentionLoadMoreError={mentionLoadMoreError}
        onLoadMoreMentionUsers={onLoadMoreMentionUsers}
        context={context}
        maxLength={maxLength}
        autoFocus={autoFocus}
        onFocusChange={setIsEditorFocused}
        alwaysVisible={alwaysVisible}
        initialContent={initialContent}
        initialFiles={initialFiles}
        initialDraftKey={initialDraftKey}
        draftScope={draftScope}
        onDraftRestored={onDraftRestored}
        onDraftRestorationSettled={onDraftRestorationSettled}
        onDraftStorageError={onDraftStorageError}
        draftTargetUnavailable={draftTargetUnavailable}
        submitIconOnly={submitIconOnly}
      />
    </div>
  );
};
